<script>
import { mapActions, mapState } from 'vuex';
import {
  ReloadOutlined,
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  FireOutlined,
  StopOutlined,
} from '@ant-design/icons-vue';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';

const b = bem('heatmaps-page');

export default {
  components: {
    PageHeader,
    ReloadOutlined,
    DesktopOutlined,
    TabletOutlined,
    MobileOutlined,
    FireOutlined,
    StopOutlined,
  },
  data() {
    return {
      title: 'Heatmaps',
      filters: {
        period: '7d',
        dateRange: [],
        deviceType: 'desktop',
        pagePath: null,
      },
      activeTab: 'clicks',
      periodOptions: [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: 'custom', label: 'Custom' },
      ],
      deviceTypes: [
        { value: 'desktop', label: 'Desktop', icon: DesktopOutlined },
        { value: 'tablet', label: 'Tablet', icon: TabletOutlined },
        { value: 'mobile', label: 'Mobile', icon: MobileOutlined },
      ],
      heatmapCanvas: null,
    };
  },
  computed: {
    ...mapState({
      pages: (state) => state.analytics.heatmapPages,
      pagesTotal: (state) => state.analytics.heatmapPagesTotal,
      pagesLoading: (state) => state.analytics.heatmapPagesLoading,
      clicks: (state) => state.analytics.heatmapClicks,
      clicksLoading: (state) => state.analytics.heatmapClicksLoading,
    }),
    filterParams() {
      const params = {
        period: this.filters.period,
        deviceType: this.filters.deviceType,
      };
      if (this.filters.period === 'custom' && this.filters.dateRange.length === 2) {
        params.startDate = this.filters.dateRange[0].toISOString();
        params.endDate = this.filters.dateRange[1].toISOString();
      }
      if (this.filters.pagePath) {
        params.pagePath = this.filters.pagePath;
      }
      return params;
    },
    pageOptions() {
      return this.pages.map((p) => ({
        value: p.pagePath,
        label: `${p.pagePath} (${p.clickCount} clicks)`,
      }));
    },
    clicksData() {
      if (!this.clicks) return { total: [], rage: [], dead: [] };
      return {
        total: this.clicks.clicks || [],
        rage: (this.clicks.clicks || []).filter((c) => c.clickType === 'rage'),
        dead: (this.clicks.clicks || []).filter((c) => c.clickType === 'dead'),
      };
    },
    currentClicksData() {
      switch (this.activeTab) {
        case 'rage':
          return this.clicksData.rage;
        case 'dead':
          return this.clicksData.dead;
        default:
          return this.clicksData.total;
      }
    },
    topElements() {
      if (!this.clicks?.topElements) return [];
      return this.clicks.topElements;
    },
    deviceDimensions() {
      switch (this.filters.deviceType) {
        case 'mobile':
          return { width: 375, height: 667 };
        case 'tablet':
          return { width: 768, height: 1024 };
        default:
          return { width: 1200, height: 800 };
      }
    },
  },
  watch: {
    currentClicksData() {
      this.$nextTick(() => {
        this.renderHeatmap();
      });
    },
  },
  methods: {
    b,
    ...mapActions('analytics', ['getHeatmapPages', 'getHeatmapClicks']),
    loadPages() {
      this.getHeatmapPages(this.filterParams);
    },
    loadClicks() {
      if (!this.filters.pagePath) return;
      this.getHeatmapClicks({
        ...this.filterParams,
        clickType: this.activeTab === 'clicks' ? null : this.activeTab,
      });
    },
    onFilterChange() {
      this.loadPages();
      if (this.filters.pagePath) {
        this.loadClicks();
      }
    },
    onPeriodChange() {
      if (this.filters.period !== 'custom') {
        this.filters.dateRange = [];
        this.onFilterChange();
      }
    },
    onDateRangeChange() {
      if (this.filters.dateRange.length === 2) {
        this.onFilterChange();
      }
    },
    onDeviceChange(device) {
      this.filters.deviceType = device;
      this.onFilterChange();
    },
    onPageSelect(pagePath) {
      this.filters.pagePath = pagePath;
      this.loadClicks();
    },
    onTabChange() {
      if (this.filters.pagePath) {
        this.renderHeatmap();
      }
    },
    renderHeatmap() {
      const canvas = this.$refs.heatmapCanvas;
      if (!canvas || !this.currentClicksData.length) return;

      const ctx = canvas.getContext('2d');
      const { width, height } = this.deviceDimensions;

      canvas.width = width;
      canvas.height = height;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw background
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, width, height);

      // Draw placeholder text
      ctx.fillStyle = '#999';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Page screenshot would appear here', width / 2, height / 2 - 20);

      // Draw heatmap points
      this.currentClicksData.forEach((click) => {
        const x = click.xRatio * width;
        const y = click.yRatio * height;
        const radius = 20;

        // Create radial gradient for heatmap effect
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

        if (click.clickType === 'rage') {
          gradient.addColorStop(0, 'rgba(255, 0, 0, 0.8)');
          gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
        } else if (click.clickType === 'dead') {
          gradient.addColorStop(0, 'rgba(255, 165, 0, 0.8)');
          gradient.addColorStop(1, 'rgba(255, 165, 0, 0)');
        } else {
          gradient.addColorStop(0, 'rgba(0, 100, 255, 0.6)');
          gradient.addColorStop(1, 'rgba(0, 100, 255, 0)');
        }

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw click count overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(10, 10, 150, 30);
      ctx.fillStyle = '#fff';
      ctx.font = '14px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`${this.currentClicksData.length} clicks`, 20, 30);
    },
    getClickTypeColor(type) {
      switch (type) {
        case 'rage':
          return '#ff4d4f';
        case 'dead':
          return '#faad14';
        default:
          return '#1890ff';
      }
    },
  },
  mounted() {
    this.loadPages();
  },
};
</script>

<template>
  <div :class="b()">
    <page-header :title="title">
      <template #actions>
        <a-button @click="onFilterChange">
          <reload-outlined />
          Refresh
        </a-button>
      </template>
    </page-header>

    <!-- Filters -->
    <a-card :class="b('filters')">
      <a-row :gutter="[16, 16]" align="middle">
        <a-col :xs="24" :sm="12" :md="6">
          <a-select
            v-model:value="filters.period"
            :options="periodOptions"
            placeholder="Period"
            style="width: 100%"
            @change="onPeriodChange"
          />
        </a-col>
        <a-col v-if="filters.period === 'custom'" :xs="24" :sm="12" :md="6">
          <a-range-picker
            v-model:value="filters.dateRange"
            :show-time="false"
            style="width: 100%"
            @change="onDateRangeChange"
          />
        </a-col>
        <a-col :xs="24" :sm="12" :md="8">
          <a-select
            v-model:value="filters.pagePath"
            :options="pageOptions"
            placeholder="Select page"
            style="width: 100%"
            show-search
            :filter-option="(input, option) => (
              option.label.toLowerCase().includes(input.toLowerCase())
            )"
            @change="onPageSelect"
          />
        </a-col>
        <a-col :xs="24" :sm="12" :md="6">
          <a-radio-group v-model:value="filters.deviceType" @change="onFilterChange">
            <a-radio-button
              v-for="device in deviceTypes"
              :key="device.value"
              :value="device.value"
            >
              <component :is="device.icon" /> {{ device.label }}
            </a-radio-button>
          </a-radio-group>
        </a-col>
      </a-row>
    </a-card>

    <a-row :gutter="[16, 16]">
      <!-- Heatmap Visualization -->
      <a-col :xs="24" :lg="16">
        <a-card :loading="clicksLoading">
          <template #title>
            <a-tabs v-model:activeKey="activeTab" @change="onTabChange">
              <a-tab-pane key="clicks" tab="All Clicks" />
              <a-tab-pane key="rage">
                <template #tab>
                  <fire-outlined style="color: #ff4d4f" /> Rage Clicks
                </template>
              </a-tab-pane>
              <a-tab-pane key="dead">
                <template #tab>
                  <stop-outlined style="color: #faad14" /> Dead Clicks
                </template>
              </a-tab-pane>
            </a-tabs>
          </template>

          <div v-if="!filters.pagePath" :class="b('placeholder')">
            <a-empty description="Select a page to view heatmap" />
          </div>

          <div v-else :class="b('heatmap-container')">
            <div
              :class="[b('device-preview'), b(`device-preview--${filters.deviceType}`)]"
            >
              <canvas
                ref="heatmapCanvas"
                :class="b('heatmap-canvas')"
              />
            </div>
          </div>
        </a-card>
      </a-col>

      <!-- Stats Panel -->
      <a-col :xs="24" :lg="8">
        <!-- Click Statistics -->
        <a-card title="Click Statistics" :class="b('stats-card')">
          <a-row :gutter="[16, 16]">
            <a-col :span="8">
              <a-statistic
                title="Total"
                :value="clicksData.total.length"
                :value-style="{ color: '#1890ff' }"
              />
            </a-col>
            <a-col :span="8">
              <a-statistic
                title="Rage"
                :value="clicksData.rage.length"
                :value-style="{ color: '#ff4d4f' }"
              />
            </a-col>
            <a-col :span="8">
              <a-statistic
                title="Dead"
                :value="clicksData.dead.length"
                :value-style="{ color: '#faad14' }"
              />
            </a-col>
          </a-row>
        </a-card>

        <!-- Top Clicked Elements -->
        <a-card title="Top Clicked Elements" :class="b('stats-card')">
          <div v-if="!topElements.length">
            <a-empty description="No data" />
          </div>
          <div v-else :class="b('elements-list')">
            <div
              v-for="(element, index) in topElements"
              :key="index"
              :class="b('element-item')"
            >
              <div :class="b('element-rank')">{{ index + 1 }}</div>
              <div :class="b('element-info')">
                <div :class="b('element-selector')">{{ element.elementSelector }}</div>
                <div :class="b('element-text')">{{ element.elementText || '-' }}</div>
              </div>
              <div :class="b('element-count')">
                <a-tag :color="getClickTypeColor(element.mostCommonType)">
                  {{ element.clickCount }}
                </a-tag>
              </div>
            </div>
          </div>
        </a-card>

        <!-- Pages List -->
        <a-card title="Pages with Most Clicks" :loading="pagesLoading" :class="b('stats-card')">
          <div v-if="!pages.length">
            <a-empty description="No pages found" />
          </div>
          <div v-else :class="b('pages-list')">
            <div
              v-for="(page, index) in pages.slice(0, 10)"
              :key="index"
              :class="[b('page-item'), { active: filters.pagePath === page.pagePath }]"
              role="button"
              tabindex="0"
              @click="onPageSelect(page.pagePath)"
              @keydown.enter="onPageSelect(page.pagePath)"
            >
              <div :class="b('page-path')">{{ page.pagePath }}</div>
              <div :class="b('page-stats')">
                <span>{{ page.clickCount }} clicks</span>
                <span v-if="page.rageCount" style="color: #ff4d4f">
                  {{ page.rageCount }} rage
                </span>
              </div>
            </div>
          </div>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<style lang="scss">
.heatmaps-page {
  padding: 24px;

  &__filters {
    margin-bottom: 16px;
  }

  &__placeholder {
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__heatmap-container {
    display: flex;
    justify-content: center;
    padding: 20px;
    background: #f0f0f0;
    border-radius: 8px;
    overflow: auto;
  }

  &__device-preview {
    background: #fff;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

    &--mobile {
      width: 375px;
      border-radius: 30px;
      padding: 10px;
      background: #1a1a1a;

      .heatmaps-page__heatmap-canvas {
        border-radius: 20px;
      }
    }

    &--tablet {
      width: 768px;
      border-radius: 20px;
      padding: 15px;
      background: #1a1a1a;

      .heatmaps-page__heatmap-canvas {
        border-radius: 8px;
      }
    }

    &--desktop {
      border-radius: 8px;
      overflow: hidden;
    }
  }

  &__heatmap-canvas {
    display: block;
    background: #fff;
  }

  &__stats-card {
    margin-bottom: 16px;
  }

  &__elements-list {
    max-height: 300px;
    overflow-y: auto;
  }

  &__element-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  &__element-rank {
    width: 24px;
    height: 24px;
    line-height: 24px;
    text-align: center;
    background: #1890ff;
    color: #fff;
    border-radius: 50%;
    font-size: 12px;
    margin-right: 12px;
  }

  &__element-info {
    flex: 1;
    overflow: hidden;
  }

  &__element-selector {
    font-size: 12px;
    font-family: monospace;
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__element-text {
    font-size: 11px;
    color: #999;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__pages-list {
    max-height: 400px;
    overflow-y: auto;
  }

  &__page-item {
    padding: 12px;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: 4px;
    border: 1px solid transparent;

    &:hover {
      background: #f5f5f5;
    }

    &.active {
      background: #e6f7ff;
      border-color: #1890ff;
    }
  }

  &__page-path {
    font-weight: 500;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__page-stats {
    font-size: 12px;
    color: #666;
    display: flex;
    gap: 12px;
  }
}
</style>
